import { i18n } from 'boot/i18n';
import { useComponentDialog } from 'src/composables/componentDialog';
import { notifyNegative, notifySuccess } from 'src/composables/interactions';
import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';
import { getFilteredElements, hydrateFilters } from 'src/utils/FilterUtils';

import { createCareModelCohortImpactReview } from '../../application/useCases/createCareModelCohortImpactReview';
import SuggestedCohortUpdatesDialog from '../components/SuggestedCohortUpdatesDialog.vue';
import { COHORT_DIALOG_ACTIONS } from '../constants/cohortDialogActions';
import { useCareModelReviewAssignments } from './useCareModelReviewAssignments';
import { useCohortsAnalytics } from './useCohortsAnalytics';
import { useCohortsFiltersColumns } from './useCohortsFiltersColumns';

/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').CareModelCohort
 * } CareModelCohort */
/** @typedef {
 *   import('../../domain/entities/CareModelCohortImpactReview.types').ImpactedCareModelCohortAssignment
 * } ImpactedCareModelCohortAssignment */

/**
 * @param {Object} args
 * @param {Function} args.ensureClientsLoaded
 * @param {Function} args.saveOneCohort
 * @param {Function} args.deleteOneCohort
 * @param {Function} args.setCohorts
 */
// eslint-disable-next-line import/prefer-default-export
export function useCohortSaveWorkflow({
  ensureClientsLoaded,
  saveOneCohort,
  deleteOneCohort,
  setCohorts,
}) {
  const authStore = useAuthStore();
  const clientsStore = useClientsStore();
  const { columns } = useCohortsFiltersColumns();
  const { openComponentDialog } = useComponentDialog();

  const { getImpactedClients } = createCareModelCohortImpactReview(
    hydrateFilters,
    getFilteredElements,
  );
  const { assignClients } = useCareModelReviewAssignments();
  const { trackCohortSaved, trackCohortDeleted } = useCohortsAnalytics();

  /**
   * @param {ImpactedCareModelCohortAssignment[]} impactedClients
   * @param {CareModelCohort[]} updatedCohorts
   * @return {Promise<ImpactedCareModelCohortAssignment[]>}
   */
  async function openSuggestedUpdatesDialog({ impactedClients, updatedCohorts }) {
    return new Promise((resolve) => {
      openComponentDialog(
        SuggestedCohortUpdatesDialog,
        {
          rows: impactedClients,
          cohorts: updatedCohorts,
        },
        (selectedRows) => resolve(selectedRows || []),
        () => resolve([]),
      );
    });
  }

  /**
   * A cohort gets its ID assigned while being saved, so a created one is only known by being new.
   *
   * @param {CareModelCohort[]} nextCohorts
   * @param {CareModelCohort[]} currentCohorts
   * @return {string|null}
   */
  function getCreatedCohortId(nextCohorts, currentCohorts) {
    return nextCohorts.find((cohort) => (
      !currentCohorts.some((currentCohort) => currentCohort.id === cohort.id)
    ))?.id ?? null;
  }

  /**
   * @param {string} action
   * @param {CareModelCohort} cohort
   * @param {CareModelCohort[]} currentCohorts
   * @param {string} cohortEditAttemptId - ID of a single open-to-close pass over the cohort dialog.
   * @return {Promise<void>}
   */
  async function handleEditCohortResult({ action, cohort, currentCohorts, cohortEditAttemptId }) {
    try {
      await ensureClientsLoaded();

      if (action === COHORT_DIALOG_ACTIONS.DELETE) {
        const { cohorts, unassignedClientIds } = await deleteOneCohort({
          clinicId: authStore.userDetails.clinic,
          currentCohorts,
          cohortId: cohort.id,
          clients: clientsStore.regularClients,
        });

        setCohorts(cohorts);

        if (unassignedClientIds.length > 0) {
          await clientsStore.initializeClients();
        }

        trackCohortDeleted({
          cohortEditAttemptId,
          cohortId: cohort.id,
          filters: cohort.filters,
          unassignedClientsCount: unassignedClientIds.length,
        });
        notifySuccess(i18n.t('settings.careModel.cohorts.notifyDeleteSuccess'));
        return;
      }

      const isCreate = !cohort.id;

      const nextCohorts = await saveOneCohort({
        clinicId: authStore.userDetails.clinic,
        currentCohorts,
        cohort,
      });

      setCohorts(nextCohorts);
      trackCohortSaved({
        cohortEditAttemptId,
        cohortId: isCreate ? getCreatedCohortId(nextCohorts, currentCohorts) : cohort.id,
        isCreate,
        filters: cohort.filters,
      });

      const impactedClients = getImpactedClients({
        clients: clientsStore.regularClients,
        cohorts: nextCohorts,
        columns: columns.value,
      });

      if (impactedClients.length > 0) {
        const selectedRows = await openSuggestedUpdatesDialog({
          impactedClients,
          updatedCohorts: nextCohorts,
        });

        if (selectedRows.length > 0) {
          await assignClients({
            assignments: selectedRows.map((row) => ({
              clientId: row.client.id,
              cohortId: row.suggestedCohort.id,
            })),
          });

          await clientsStore.initializeClients();
        }
      }

      notifySuccess(i18n.t('settings.careModel.cohorts.notifySaveSuccess'));
    } catch (err) {
      notifyNegative(i18n.t('common.errors.generic'));
    }
  }

  return {
    handleEditCohortResult,
  };
}
